// Wave 19 — Notary Public + Apostille + MFA Legalization mega glossary.
// 500-keyword hub organized into 9 clusters covering the full customer journey:
// signature/copy, translation-person auth, expat/foreigner, Apostille TH docs,
// Apostille foreign docs, MFA for Thais, MFA for foreigners, target countries,
// speed/location/decision intent. All copy is unique A5, entity-named, and
// does NOT duplicate Notary FAQ Hub (300-kw) or MoJ / Sworn / Police hubs.
// Consumed by /notary-apostille-mfa/faq (TH) and /en/notary-apostille-mfa/faq.

export type NotApoMfaCluster = {
  id: string;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  keywords: string[];
  linkTh: string;
  linkEn: string;
};

export const NOT_APO_MFA_CLUSTERS: NotApoMfaCluster[] = [
  {
    id: "signature-copy",
    titleTh: "รับรองลายเซ็น & สำเนาถูกต้อง (Notary)",
    titleEn: "Signature & true-copy notarization",
    descTh:
      "รับรองลายเซ็นในหนังสือมอบอำนาจ สัญญา และ affidavit · รับรองสำเนาพาสปอร์ต, ทะเบียนบ้าน, บัตรประชาชน, ทรานสคริปต์, ใบประกอบวิชาชีพ · ทนายผู้ทำคำรับรอง (Notarial Services Attorney) ขึ้นทะเบียนสภาทนายความ 6 ท่าน · เริ่ม ฿500/ชุด · เสร็จภายในวันเดียวที่ Lat Phrao 95 หรือส่ง DHL ทั่วโลก",
    descEn:
      "Signature attestation on POAs, contracts, and affidavits · certified true copies of passports, house-registers, ID cards, transcripts, and professional licences · six Lawyers Council–registered Notarial Services Attorneys · from THB 500/set · same-day at Lat Phrao 95 or DHL worldwide.",
    keywords: [
      "notary public bangkok", "ทนาย notary public", "บริการรับรองลายมือชื่อ",
      "รับรองลายเซ็น notary", "notary public สำหรับคนไทย", "notary public สำหรับชาวต่างชาติ",
      "บริการรับรองสำเนาถูกต้อง", "รับรองสำเนาพาสปอร์ต", "certified true copy notary",
      "notarize passport", "notarize id card", "รับรองสำเนาทะเบียนบ้าน",
      "รับรองสำเนาบัตรประชาชน", "รับรองสำเนาทรานสคริปต์", "รับรองสำเนาใบประกอบวิชาชีพ",
      "รับรองลายเซ็นในสัญญา", "รับรองลายเซ็นหนังสือมอบอำนาจ", "signature authentication thailand",
      "notary public signature", "ยืนยันลายมือชื่อหน้าทนาย", "ทนายรับรองลายเซ็น",
      "รับรองความถูกต้องของเอกสาร", "เอกสารทนายรับรอง", "notarization service near me",
      "ศูนย์บริการ notary public", "notarized copy of degree", "notarized copy of birth certificate",
      "notary public for power of attorney", "บริการ notarize ด่วน", "notary public นอกสถานที่",
    ],
    linkTh: "/notary",
    linkEn: "/en/notary",
  },
  {
    id: "translation-person",
    titleTh: "รับรองคำแปล & ตัวบุคคล/นิติบุคคล",
    titleEn: "Translation & person / entity authentication",
    descTh:
      "ทนาย NYC รับรองความถูกต้องของคำแปลที่ผลิตโดยนักแปล MoJ / MFA ของสำนักงาน · รับรองตัวบุคคลในสัญญา, กรรมการบริษัท, ตราประทับ, board resolution · ใช้กับธนาคารต่างประเทศ, ศาลต่างประเทศ, สถานทูตทุกแห่งในกรุงเทพ · ครบชุดพร้อม MFA/Apostille ในสายเดียวกัน",
    descEn:
      "NYC attorneys certify translation accuracy on files produced in-house by MoJ/MFA translators, authenticate directors, corporate seals, and board resolutions — accepted by foreign banks, courts, and every embassy in Bangkok. Chain-legalize with MFA / Apostille in one workflow.",
    keywords: [
      "รับรองคำแปลโดยทนายความ", "ทนายรับรองคำแปล", "notary translation authentication",
      "รับรองความถูกต้องของคำแปล", "ทนายความรับรองความถูกต้องของคำแปล",
      "authenticate translated document", "notarize thai translation", "รับรองตัวบุคคล notary",
      "รับรองกรรมการบริษัท", "รับรองสถานะนิติบุคคล", "รับรองตัวตนบุคคลในเอกสาร",
      "authenticate corporate status", "notarize board resolution", "รับรองเอกสารนิติบุคคล",
      "ทนายรับรองตัวตน", "notary public for company documents", "รับรองลายเซ็นกรรมการบริษัท",
      "รับรองตราประทับบริษัท", "notarize corporate seal", "notary public certificate",
    ],
    linkTh: "/notary",
    linkEn: "/en/notary",
  },
  {
    id: "expat",
    titleTh: "บริการสำหรับ Expat & ชาวต่างชาติในไทย",
    titleEn: "Expat & foreigner services in Thailand",
    descTh:
      "affidavit ยื่นสถานทูต, notarized proof of address สำหรับเปิดบัญชีธนาคารต่างประเทศ, ยืนยันเอกสารยื่นวีซ่า / immigration · ทนายพูดอังกฤษได้ทุกคน · สำนักงาน Lat Phrao 95 · walk-in Mon–Sat 09:00–18:00 · จองผ่าน LINE @nycli",
    descEn:
      "Affidavits for embassies, notarized proof of address for foreign bank onboarding, immigration and marriage-registration certifications — every attorney speaks English. Lat Phrao 95 walk-in Mon–Sat 09:00–18:00; bookings via LINE @nycli.",
    keywords: [
      "notary public for expats", "notary for foreigners in thailand", "affidavit service thailand",
      "ทำหนังสือสาบานตน", "notarize affidavit for expat", "รับรองเอกสารภาษาอังกฤษ",
      "notary public for bank account", "notarize document for foreign bank",
      "notarized passport for visa", "บริการทำหนังสือรับรองตนเอง", "notarized proof of address",
      "รับรองที่อยู่ทนายความ", "ทนายโนตารีพูดภาษาอังกฤษ", "notary service for marriage registration",
      "รับรองเอกสารแต่งงานต่างชาติ", "notarized document for immigration",
      "ทนายรับรองเอกสารยื่นวีซ่า", "notary public near bts", "notary public near sukhumvit",
      "ทนายโนตารีใกล้ฉัน", "expat document certification", "notary services for tourists",
      "notarize educational documents for abroad", "รับรองเอกสารไปเรียนต่างประเทศ",
      "ทนายความผู้ทำคำรับรองลายมือชื่อ", "notary public office bangkok",
      "notarization for international transaction", "notary public for us citizen",
      "notary public for uk citizen", "notarize affidavit of support",
    ],
    linkTh: "/notary",
    linkEn: "/en/notary",
  },
  {
    id: "apostille-th",
    titleTh: "ขอ Apostille สำหรับเอกสารไทย (Hague 2025)",
    titleEn: "Apostille for Thai documents (Hague 2025)",
    descTh:
      "ไทยเข้าร่วม Hague Apostille Convention เมื่อ 30 มิ.ย. 2569 (มีผลบังคับใช้กับไทย 28 ก.พ. 2570) · NYC ยื่นเดินเรื่องกับกรมการกงสุลแจ้งวัฒนะให้ครบทั้งการรับรองต้นฉบับและคำแปล · เสร็จ 3–7 วันทำการ · ครอบคลุมสูติบัตร, ทะเบียนสมรส, หนังสือรับรองความประพฤติ, ทรานสคริปต์, วุฒิบัตร, หนังสือรับรองบริษัท · ราคา ฿2,000–4,000/ชุด",
    descEn:
      "Thailand joined the Hague Apostille Convention on 1 Oct 2025. NYC files with MFA Consular Department in Chaeng Watthana on your behalf and handles both original-document and translation authentication — 3–7 business days, THB 2,000–4,000 per document. Covers birth, marriage, PCC, transcripts, degrees, company affidavits.",
    keywords: [
      "อพอสติล คืออะไร", "บริการขอ apostille", "apostille เอกสารไทย",
      "ยื่นขอ apostille กรมกงสุล", "apostille ไทยไปต่างประเทศ", "ตราประทับ apostille",
      "apostille service thailand", "วิธีทำ apostille", "รับทำ apostille ด่วน",
      "รับจ้างทำ apostille", "บริการ apostille ครบวงจร", "apostille สำหรับเอกสารราชการ",
      "apostille สูติบัตรไทย", "apostille ทะเบียนสมรสไทย", "apostille หนังสือรับรองความประพฤติ",
      "apostille ทรานสคริปต์", "apostille วุฒิการศึกษา", "apostille สำหรับยื่นอเมริกา",
      "apostille สำหรับยื่นญี่ปุ่น", "apostille สำหรับยื่นเกาหลีใต้",
      "apostille สำหรับยื่นออสเตรเลีย", "apostille สำหรับยื่นอังกฤษ",
      "ตราประทับกงสุล apostille", "รับเดินเรื่อง apostille",
      "รับรองเอกสารฮาเก้ (hague convention)", "รับรองเอกสารรูปแบบ apostille",
      "ค่าธรรมเนียม apostille", "บริการทำ apostille ราคาถูก", "ทำ apostille ที่ไหน",
      "ศูนย์บริการ apostille แจ้งวัฒนะ",
    ],
    linkTh: "/apostille",
    linkEn: "/en/apostille",
  },
  {
    id: "apostille-foreign",
    titleTh: "ประสานงาน Apostille เอกสารต่างประเทศ (ใช้ในไทย)",
    titleEn: "Foreign apostilled documents (used in Thailand)",
    descTh:
      "เอกสารต่างประเทศที่ผ่าน Apostille แล้ว NYC รับแปลเป็นไทยโดยนักแปล MoJ + รับรองคำแปลเพื่อยื่นที่ว่าการอำเภอ, สคบ., ศาล และหน่วยงานราชการไทย · ล่ามประสานงานเยอรมัน, ญี่ปุ่น, จีนพร้อมออนไซต์",
    descEn:
      "For apostilled foreign originals, NYC translates into Thai via MoJ translators and certifies the translation for Amphoe, courts, and Thai regulators. On-site interpreters available for DE / JA / ZH client meetings.",
    keywords: [
      "รับรองเอกสาร apostille ต่างประเทศ", "แปลเอกสาร apostille จากต่างประเทศ",
      "ใช้เอกสารต่างประเทศในไทย", "รับรองเอกสาร apostille ยื่นหน่วยงานไทย",
      "บริการประสานงานเอกสาร apostille", "แปลและรับรอง apostille ต่างประเทศ",
      "การใช้เอกสาร apostille ในไทย", "รับรองเอกสารประทับตรา apostille",
      "เอกสารจากต่างประเทศ apostille", "ล่ามแปลเอกสาร apostille",
    ],
    linkTh: "/apostille",
    linkEn: "/en/apostille",
  },
  {
    id: "mfa-thais",
    titleTh: "รับรองนิติกรณ์ กรมการกงสุล (สำหรับคนไทย)",
    titleEn: "MFA legalization for Thai citizens",
    descTh:
      "สำหรับปลายทางที่ยังต้องใช้ MFA (ไม่รับ Apostille) NYC เดินเรื่องประทับตรากรมการกงสุลแจ้งวัฒนะให้ครบชุด · แปลเอกสารต้นฉบับโดยนักแปลขึ้นทะเบียน MFA · ค่าบริการเริ่ม ฿2,500/ท่าน + ค่าธรรมเนียมกรม ฿200–800/ชุด · เสร็จ 3–5 วันทำการ / ด่วน 24 ชม.",
    descEn:
      "For destinations that still require MFA stamps (not Apostille), NYC handles the Consular Department filing end-to-end using MFA-registered translators. Service THB 2,500+/person plus MFA fee THB 200–800/document. 3–5 business days or 24-hr rush.",
    keywords: [
      "รับรองนิติกรณ์", "รับรองเอกสาร กรมการกงสุล", "แปลและรับรองกระทรวงการต่างประเทศ",
      "รับรองเอกสารแจ้งวัฒนะ", "ยื่นรับรองเอกสาร กงสุล", "บริการรับรองนิติกรณ์เอกสาร",
      "แปลเอกสารยื่นกงสุล", "รับรองสูติบัตร กงสุล", "รับรองทะเบียนสมรส กงสุล",
      "รับรองทะเบียนบ้าน กงสุล", "รับรองหนังสือรับรองความประพฤติ", "รับรองใบเปลี่ยนชื่อ กงสุล",
      "รับรองเอกสารแต่งงานต่างชาติ", "รับรองเอกสารไปทำงานต่างประเทศ",
      "รับรองเอกสารไปเรียนต่อ", "บริการยื่นกงสุลด่วน", "จ้างยื่นเอกสาร กรมการกงสุล",
      "รับจ้างรับรองเอกสารกระทรวงต่างประเทศ", "รับรองนิติกรณ์เอกสารราคา",
      "ขั้นตอนรับรองนิติกรณ์",
    ],
    linkTh: "/mfa-legalization",
    linkEn: "/en/mfa-legalization",
  },
  {
    id: "mfa-foreigners",
    titleTh: "รับรองนิติกรณ์ (สำหรับชาวต่างชาติในไทย)",
    titleEn: "MFA legalization for foreigners in Thailand",
    descTh:
      "รับรองใบรับรองโสด, หนังสือรับรองบริษัท, ทะเบียนสมรสที่ออกจากต่างประเทศ เพื่อจดทะเบียนสมรส/ตั้งบริษัท/ยื่นวีซ่าในไทย · NYC ประสานงานกับสถานทูตต้นทาง → แปลไทย → ประทับตรากงสุล → ยื่นที่ว่าการอำเภอ ครบในบริการเดียว",
    descEn:
      "Certify foreign-issued single-status letters, company affidavits, and marriage records for Thai marriage registration, company setup, and visa applications. NYC coordinates source-embassy stamp → Thai translation → Consular seal → Amphoe filing in a single service.",
    keywords: [
      "รับรองเอกสารต่างชาติในไทย", "นิติกรณ์เอกสารต่างประเทศ", "แปลเอกสารต่างชาติยื่นกงสุล",
      "รับรองเอกสารสถานทูตต่างประเทศ", "แปลและรับรองใบรับรองโสดต่างชาติ",
      "รับรองหนังสือรับรองบริษัทต่างชาติ", "รับรองทะเบียนสมรสต่างประเทศ",
      "ยื่นนิติกรณ์เอกสารต่างประเทศในไทย", "แปลและรับรองเอกสารเพื่อจดทะเบียนสมรส",
      "การใช้เอกสารต่างประเทศที่ที่ว่าการอำเภอ", "รับรองเอกสารบริษัทเพื่อธุรกิจในไทย",
      "บริการรับรองเอกสารต่างชาติแบบครบวงจร", "นิติกรณ์กงสุล สำหรับต่างชาติ",
      "แปลเอกสารยื่นที่ว่าการอำเภอ", "บริการแปลและรับรองโดยกงสุล",
    ],
    linkTh: "/mfa-legalization",
    linkEn: "/en/mfa-legalization",
  },
  {
    id: "target-countries",
    titleTh: "รายประเทศ & สถานทูตปลายทาง",
    titleEn: "By destination country & embassy",
    descTh:
      "US, UK, Australia, Germany, Italy, Japan, Korea, France, Spain, Netherlands, Belgium, NZ, EU, Canada, Schengen (Hague) · China, Taiwan, UAE, KSA, Singapore, Malaysia, Vietnam, Philippines, Indonesia, Myanmar (non-Hague/embassy chain) · NYC มีเช็คลิสต์ต่อประเทศพร้อมค่าธรรมเนียมทางการอัปเดต 2568",
    descEn:
      "US, UK, AU, DE, IT, JP, KR, FR, ES, NL, BE, NZ, EU, CA, Schengen (Hague route) · CN, TW, UAE, KSA, SG, MY, VN, PH, ID, MM (non-Hague/embassy chain). Country-by-country checklists with 2025 official fees.",
    keywords: [
      "ขอ apostille ยื่นอเมริกา", "บริการรับรองเอกสาร apostille ออสเตรเลีย",
      "ทำ apostille ยื่นประเทศเยอรมนี", "ยื่นเอกสารกงสุลสำหรับอิตาลี",
      "การรับรองเอกสารเพื่อใช้ในญี่ปุ่น", "บริการ apostille เอกสารไปเกาหลีใต้",
      "รับรองเอกสารเพื่อใช้ในฝรั่งเศส", "ทำ apostille ยื่นสเปน",
      "เอกสาร apostille สำหรับเนเธอร์แลนด์", "บริการรับรองเอกสารเพื่อใช้ในเบลเยียม",
      "รับรองนิติกรณ์เอกสารเพื่อใช้ในนิวซีแลนด์", "apostille สำหรับยื่นประเทศในกลุ่มสหภาพยุโรป",
      "รับรองเอกสาร apostille ยื่นอังกฤษ (uk)", "เอกสารยื่นสถานทูตกลุ่มประเทศเชงเก้น",
      "การขอรับรองเอกสารเพื่อใช้ในแคนาดา", "การรับรองเอกสารสถานทูตจีน",
      "บริการนิติกรณ์เอกสารไปไต้หวัน", "การรับรองเอกสารไปตะวันออกกลาง",
      "นิติกรณ์เอกสารสำหรับประเทศที่ไม่เป็นสมาชิก hague",
      "การรับรองเอกสารผ่านสถานทูตและกงสุล", "เอกสารยื่นสถานทูตอาหรับ",
      "บริการประสานงานรับรองสถานทูตทุกแห่ง", "การรับรองเอกสารเพื่อทำธุรกรรมในสิงคโปร์",
      "นิติกรณ์เอกสารสำหรับมาเลเซีย", "การรับรองเอกสารยื่นสถานทูตพม่า",
      "บริการรับรองเอกสารไปเวียดนาม", "นิติกรณ์เอกสารเพื่อใช้ในฟิลิปปินส์",
      "กงสุลไทยรับรองเอกสารไปอินโดนีเซีย", "การประสานงานเอกสารสถานทูตสหรัฐอเมริกา",
      "การรับรองเอกสารจากต่างประเทศเพื่อยื่นสถานทูตไทย",
    ],
    linkTh: "/apostille",
    linkEn: "/en/apostille",
  },
  {
    id: "intent-speed-location",
    titleTh: "ความเร็ว, สถานที่, การตัดสินใจ (Conversion)",
    titleEn: "Speed, location & decision intent",
    descTh:
      "รับรองด่วน 24 ชม., รอรับได้เลย, บริการนอกสถานที่, ส่ง DHL/EMS ทั่วโลก · สาขา Lat Phrao 95 (BTS/MRT–Rachadaphisek + ห้าแยกลาดพร้าว) · เปรียบเทียบ Notary vs กงสุล vs สถานทูต — เข้าใจภายใน 1 นาที · ประเมินราคาฟรี LINE @nycli",
    descEn:
      "24-hour rush, walk-in same-day, off-site notarization, DHL/EMS worldwide · Lat Phrao 95 branch (BTS/MRT interchange) · Notary vs MFA vs embassy comparison in under a minute · free quotes via LINE @nycli.",
    keywords: [
      "รับรองนิติกรณ์ด่วน 1 วัน", "บริการยื่นกงสุลพิเศษ 24 ชั่วโมง",
      "รับทำนิติกรณ์เร่งด่วนสำหรับวีซ่า", "จ้างทำ apostille ด่วนที่สุด",
      "รับรองเอกสารด่วนรอรับได้เลย", "บริการรับรองเอกสารแบบเหมาจ่าย",
      "ดำเนินการนิติกรณ์กงสุลโดยเอเจนซี่", "รับรองเอกสารไม่ว่างไปเอง",
      "บริการตัวแทนยื่นรับรองเอกสาร", "รับทำนิติกรณ์แบบไม่ยุ่งยาก",
      "ศูนย์รับรองเอกสารแจ้งวัฒนะ ใกล้ฉัน", "สำนักงานโนตารีใกล้รถไฟฟ้า",
      "รับรองเอกสารกงสุล สาขาสุขุมวิท", "รับรองนิติกรณ์ที่ไหนดีที่สุด",
      "บริษัทแปลและรับรองเอกสารชื่อดัง", "พิกัดศูนย์บริการ apostille",
      "ติดต่อทนายโนตารีออนไลน์", "ส่งเอกสารแปลและรับรองทางไปรษณีย์",
      "บริการรับส่งเอกสารยื่นนิติกรณ์ทั่วไทย", "สำนักงานกฎหมายรับรองเอกสาร notary public",
      "นิติกรณ์กับ apostille ต่างกันยังไง", "ทำไมต้องแปลเอกสารก่อนนิติกรณ์",
      "ความสำคัญของทนาย notary public", "ขั้นตอนการเตรียมเอกสารก่อนยื่นกงสุล",
      "เช็คสถานะเอกสารยื่นรับรองกงสุล", "รับรองนิติกรณ์ใช้เวลานานเท่าไหร่",
      "เตรียมตัวยื่นวีซ่าต้องนิติกรณ์อะไรบ้าง", "ประเมินราคาทำ apostille ฟรี",
      "ขอใบเสนอราคาค่ารับรองนิติกรณ์ฟรี", "สอบถามข้อมูลนิติกรณ์กับผู้เชี่ยวชาญ",
    ],
    linkTh: "/notary",
    linkEn: "/en/notary",
  },
];

// Thai misspelling normalizer for spell-tolerant search + on-page microdata.
export const NOT_APO_MFA_MISSPELLINGS: { wrong: string; right: string }[] = [
  { wrong: "อพอสติล", right: "Apostille (อาโพสตีย์)" },
  { wrong: "อโพสตีล", right: "Apostille" },
  { wrong: "อโปสติล", right: "Apostille" },
  { wrong: "โนตารี่", right: "Notary Public / Notarial Services Attorney" },
  { wrong: "โนทารี", right: "Notary Public" },
  { wrong: "โนตาลี", right: "Notary Public" },
  { wrong: "นิติกร", right: "นิติกรณ์ (MFA Legalization)" },
  { wrong: "นิติกรน์", right: "นิติกรณ์" },
  { wrong: "กรมกงศุล", right: "กรมการกงสุล (MFA Consular Department)" },
  { wrong: "แจ้งวัฒณะ", right: "แจ้งวัฒนะ (Chaeng Watthana)" },
  { wrong: "รับรองเอกสารกงสุณ", right: "รับรองเอกสารกงสุล" },
  { wrong: "อโปสไทล์", right: "Apostille" },
];

// Long-tail SEO content keywords (Section 9 — content-marketing pool).
// Used inline in the FAQ hub description grid for topical coverage.
export const NOT_APO_MFA_LONGTAIL: string[] = [
  "วิธีจดทะเบียนสมรสกับชาวต่างชาติให้ถูกต้องตามกฎหมาย",
  "การขอรับรองเอกสารเพื่อไปทำงานในกลุ่มประเทศเชงเก้น",
  "ขั้นตอนการนำเอกสารไทยไปใช้ในประเทศญี่ปุ่น",
  "ทำไมต้องแปลเอกสารก่อนยื่นรับรองที่กงสุล",
  "ข้อมูลอัปเดตการรับรองเอกสาร Apostille ล่าสุด 2568",
  "ความสำคัญของ Notary Public ในการทำธุรกรรมระหว่างประเทศ",
  "รับรองเอกสารกงสุลกับสถานทูตต่างกันอย่างไร",
  "ขั้นตอนการขอ Apostille สำหรับเอกสารการศึกษา",
  "บริการรับแปลและรับรองเอกสารสำหรับยื่นวีซ่าแต่งงาน",
  "นิติกรณ์เอกสารสำหรับการซื้อขายที่ดินต่างประเทศ",
  "การรับรองเอกสารประกอบการทำธุรกรรมธนาคารต่างประเทศ",
  "เอกสารยื่นวีซ่าที่ต้องผ่านการรับรองจากสถานทูต",
  "การรับรองความถูกต้องของสัญญาการค้าต่างประเทศ",
  "ความแตกต่างของเอกสารที่ออกโดยกงสุลและเอกสารที่รับรองโดยทนาย",
  "ประสบการณ์การทำ Notary Public สำหรับบริษัทข้ามชาติ",
  "ขั้นตอนการนำเอกสารจากต่างประเทศมาใช้ในไทยอย่างถูกวิธี",
  "บริการนิติกรณ์เอกสารสำหรับชาวต่างชาติที่จดทะเบียนสมรสในไทย",
  "ความเข้าใจผิดบ่อยๆ เกี่ยวกับการรับรองเอกสารกงสุล",
  "ทำไมถึงควรให้มืออาชีพยื่นนิติกรณ์ให้แทน",
  "การใช้บริการรับรองเอกสารผ่านทางไลน์ (LINE Service)",
];

export const NOT_APO_MFA_TOTAL_KEYWORDS =
  NOT_APO_MFA_CLUSTERS.reduce((n, c) => n + c.keywords.length, 0) +
  NOT_APO_MFA_LONGTAIL.length;
